import axios from "axios"
import React, { useEffect, useMemo, useState } from "react"
import { Link as RouterLink, useLocation } from "react-router-dom"
import { decodeToken, isExpired } from "react-jwt"
import { useTranslation } from "react-i18next"

import Alert from "@material-ui/lab/Alert"
import Box from "@material-ui/core/Box"
import CircularProgress from "@material-ui/core/CircularProgress"
import FormControl from "@material-ui/core/FormControl"
import Button from "@material-ui/core/Button"
import Typography from "@material-ui/core/Typography"
import Link from "@material-ui/core/Link"

import { AuthApi } from "common/api"
import { AuthLayout } from "./AuthLayout"
import { PasswordTextFieldLogin } from "components/partials"

const isVerificationToken = token => {
    return token && "email" in token && typeof token.email === "string"
}

const VerificationController = () => {
    const { t } = useTranslation()
    const { search } = useLocation()
    const [token, setToken] = useState(null)
    const [verifyResponse, setVerifyResponse] = useState(null)
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState(null)

    const tokenError = useMemo(
        () => ({
            Invalid: t("view.accountVerification.errorTokenInvalid"),
            FailedVerification: t("view.accountVerification.errorTokenVerification")
        }),
        [t]
    )

    useEffect(() => {
        setIsLoading(true)

        const token = new URLSearchParams(search).get("token") || ""
        const decoded = decodeToken(token)

        if (!isVerificationToken(decoded)) {
            setError(tokenError.Invalid)
            setIsLoading(false)
            return
        }

        setToken(token)
        if (isExpired(token)) {
            setError(tokenError.Invalid)
        }

        AuthApi.verifyEmail({ token })
            .then(response => setVerifyResponse(response.data))
            .catch(() => setError(tokenError.FailedVerification))
            .finally(() => setIsLoading(false))
    }, [search, tokenError])

    if (isLoading) {
        return (
            <AuthLayout>
                <Box
                    height={200}
                    width="100%"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                >
                    <CircularProgress size={30} />
                </Box>
            </AuthLayout>
        )
    }

    if (error || !token || !verifyResponse) {
        return (
            <AuthLayout>
                <FormControl fullWidth margin="normal">
                    <Typography style={{ color: "white" }} component="h2" variant="h6">
                        {t("errors.somethingWentWrong")}
                    </Typography>
                </FormControl>
                <FormControl>
                    <Typography style={{ color: "white" }}>{error ? error : tokenError.Invalid}</Typography>
                </FormControl>
            </AuthLayout>
        )
    }

    return <Form {...verifyResponse} />
}

const Form = props => {
    const { t } = useTranslation()
    const { user, passwordToken } = props
    const [isLoading, setIsLoading] = useState(false)
    const [isSuccess, setIsSuccess] = useState(false)
    const [error, setError] = useState(null)
    const [payload, setPayload] = useState({ password: "" })
    const [isValidPassword, setIsValidPassword] = useState(false)

    const [fontSize, setFontSize] = useState(window.innerWidth * 0.015)

    useEffect(() => {
        const handleResize = () => {
            setFontSize(window.innerWidth * 0.015);
        }

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        }
    }, [])

    const styleButton = {
        backgroundColor: "black",
        fontWeight: "bold",
        fontSize: fontSize * 0.75
    }

    const onChange = key => event => {
        setPayload({ ...payload, [key]: event.target.value })
    }

    async function onSubmit(event) {
        event.preventDefault()

        if (!isValidPassword) return

        setIsLoading(true)

        try {
            await AuthApi.resetPassword({
                password: payload.password,
                token: String(passwordToken)
            })
            setIsSuccess(true)
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const message = error.response?.data.message
                setError(
                    message ? message : t("view.accountVerification.errorInvalidPassword")
                )
            } else {
                setError(t("errors.somethingWentWrong"))
            }
        } finally {
            setIsLoading(false)
        }
    }

    function onValidated(hasError) {
        setIsValidPassword(hasError === false)
    }

    if (!passwordToken) {
        return <ChangePasswordForm />
    }

    if (isSuccess) {
        return <SuccessForm />
    }

    return (
        <AuthLayout>
            {error && (
                <Box width="100%" marginBottom={2}>
                    <Alert severity="error">{error}</Alert>
                </Box>
            )}

            <Box width="100%" component="form" onSubmit={onSubmit}>
                <Typography style={{ color: "white" }}>{t("view.accountVerification.setPassword")}</Typography>

                {/* Password forms should have (optionally hidden) username fields for accessibility */}
                <input
                    hidden
                    autoComplete="username"
                    defaultValue={`${user?.firstName} ${user?.lastName}`}
                />

                <FormControl fullWidth margin="normal">
                    <PasswordTextFieldLogin
                        required
                        label="Password"
                        value={payload.password}
                        onChange={onChange("password")}
                        autoComplete="new-password"
                        CheckListProps={{
                            show: true,
                            onChange: onValidated
                        }}
                    />
                </FormControl>

                <FormControl fullWidth margin="normal">
                    <Button
                        style={styleButton}
                        fullWidth
                        disabled={isLoading || !isValidPassword}
                        color="primary"
                        variant="contained"
                        type="submit"
                    >
                        {isLoading
                            ? t("view.accountVerification.setPasswordButtonLoading")
                            : t("view.accountVerification.setPasswordButton")}
                    </Button>
                </FormControl>
            </Box>
        </AuthLayout>
    )
}

const SuccessForm = () => {
    const { t } = useTranslation()

    const fontSize = window.innerWidth * 0.015

    const styleButton = {
        backgroundColor: "black",
        color: "white",
        fontWeight: "bold",
        fontSize: fontSize * 0.75
    }

    return (
        <AuthLayout>
            <FormControl fullWidth margin="normal">
                <Typography style={{ color: "white" }} component="h2" variant="h6">
                    {t("view.accountVerification.successTitle")}
                </Typography>

                <Typography style={{ color: "white" }}>
                    {t("view.accountVerification.successBody")}
                </Typography>
            </FormControl>

            <FormControl fullWidth margin="normal">
                <Button style={styleButton}
                    fullWidth
                    component={RouterLink}
                    to="/login"
                    color="primary"
                    variant="contained"
                >
                    {t("view.accountVerification.login")}
                </Button>
            </FormControl>
        </AuthLayout>
    )
}

const ChangePasswordForm = () => {
    const { t } = useTranslation()

    const fontSize = window.innerWidth * 0.015

    const styleButton = {
        backgroundColor: "black",
        color: "white",
        fontWeight: "bold",
        fontSize: fontSize * 0.75
    }

    return (
        <AuthLayout>
            <FormControl fullWidth margin="normal">
                <Typography style={{ color: "white" }} component="h2" variant="h6">
                    {t("view.accountVerification.passwordSetTitle")}
                </Typography>

                <Typography style={{ color: "white" }}>
                    {t("view.accountVerification.passwordSetBody")}
                </Typography>
            </FormControl>

            <FormControl fullWidth margin="normal">
                <Button style={styleButton}
                    fullWidth
                    component={RouterLink}
                    to="/login"
                    color="primary"
                    variant="contained"
                >
                    {t("view.accountVerification.login")}
                </Button>
            </FormControl>

            <FormControl>
                <Typography>
                    <Link style={{ color: "white" }} component={RouterLink} to="/forgot-password">
                        {t("view.accountVerification.forgotPasswordLink")}
                    </Link>
                </Typography>
            </FormControl>
        </AuthLayout>
    )
}

export default VerificationController
